import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Instagram, Shield, FileText } from "lucide-react";

const Footer = () => {
  const location = useLocation();
  const isClientRoute = location.pathname.includes('/client');

  const content = {
    tagline: isClientRoute 
      ? "Your complete wellness companion that helps you track and improve your health journey."
      : "See which accounts are slipping, then reach them before they leave.",
    trustBadge: isClientRoute
      ? "Trusted by 500+ users worldwide"
      : "Built for SaaS teams and D2C brands.",
    madeWith: isClientRoute
      ? "for health enthusiasts"
      : "for SaaS and D2C teams",
    cta: isClientRoute
      ? "Ready to start your wellness journey?"
      : "Ready to see which accounts need you?",
    instagram: isClientRoute
      ? "kolanut.ai"
      : "kolanut.ai"
  };

  return (
    <footer className="relative bg-gradient-to-br from-gray-50 via-white to-[#c4ffa0]/5 border-t border-[#c4ffa0]/20">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-64 h-64 bg-[#c4ffa0]/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-green-100/30 rounded-full blur-2xl"></div>
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main footer content */}
        <div className="py-8 lg:py-12">
          {/* Mobile: stacked layout — logo → tagline → legal → social (big) */}
          <div className="flex flex-col lg:flex-row justify-between items-center gap-8 lg:gap-12">
            {/* Logo and tagline */}
            <div className="flex flex-col items-center lg:items-start text-center lg:text-left space-y-3 order-1">
              <div className="flex items-center group">
                <div className="relative">
                  <img
                    src="/kolanut_logo.png"
                    alt="Kolanut"
                    className="h-8 w-8 object-contain transition-transform duration-300 group-hover:scale-110"
                  />
                  <div className="absolute -inset-1 bg-gradient-to-r from-[#c4ffa0] to-green-400 rounded-full opacity-0 group-hover:opacity-20 blur-sm transition-all duration-500"></div>
                </div>
                <span className="ml-2 text-xl font-bold text-gray-900">
                  Kolanut AI
                </span>
              </div>
              <p className="text-sm text-gray-600 max-w-md">
                {content.tagline}
              </p>
            </div>

            {/* Social first, then Legal — on mobile stack; on desktop one row */}
            <div className="flex flex-col sm:flex-row items-center gap-6 lg:gap-8 order-2 w-full lg:w-auto">
              {/* Social — much bigger touch targets */}
              <div className="flex items-center justify-center gap-4 sm:gap-5">
                <a
                  href="https://x.com/kolanutai"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="X (Twitter)"
                  className="flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-black transition-all duration-300 group"
                >
                  <svg className="w-6 h-6 sm:w-7 sm:h-7 transition-transform duration-300 group-hover:scale-110" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
                <a
                  href={`https://www.instagram.com/${content.instagram}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-black transition-all duration-300 group"
                >
                  <Instagram className="w-6 h-6 sm:w-7 sm:h-7 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-12" />
                </a>
              </div>

              {/* Legal links — Privacy (Shield), Terms (FileText) */}
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
                <Link
                  to="/privacy-policy"
                  className="group flex items-center text-sm text-gray-600 hover:text-black transition-all duration-300 relative"
                >
                  <Shield className="w-4 h-4 mr-1.5 flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
                  <span>Privacy Policy</span>
                  <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#c4ffa0] group-hover:w-full transition-all duration-300"></div>
                </Link>
                <Link
                  to="/terms-of-use"
                  className="group flex items-center text-sm text-gray-600 hover:text-black transition-all duration-300 relative"
                >
                  <FileText className="w-4 h-4 mr-1.5 flex-shrink-0 transition-transform duration-300 group-hover:scale-110" />
                  <span>Terms of Use</span>
                  <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#c4ffa0] group-hover:w-full transition-all duration-300"></div>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom section — extra padding so content is scrollable above fold / above floating CTA */}
        <div className="border-t border-[#c4ffa0]/20 pt-6 pb-24 md:pb-12">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            {/* Copyright */}
            <div className="flex items-center justify-center sm:justify-start text-sm text-gray-500 order-2 sm:order-1 text-center sm:text-left">
              <span>© 2026 Rezonata Labs Inc. All rights reserved.</span>
            </div>
            
            {/* Made with love indicator */}
            {/* <div className="flex items-center text-xs text-gray-400 order-1 sm:order-2 group">
              <span>Made with</span>
              <Heart className="w-3.5 h-3.5 mx-1.5 text-red-400 transition-all duration-300 group-hover:scale-125 group-hover:text-red-500" />
              <span>{content.madeWith}</span>
            </div> */}
          </div>
        </div>

        {/* Mobile-optimized contact section */}
        {/* {!isClientRoute && (
          <div className="lg:hidden border-t border-[#c4ffa0]/20 pt-6 pb-4">
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-3">
                {content.cta}
              </p>
              <a 
                href="https://calendly.com/usekinos"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-[#c4ffa0] to-[#c4ffa0]/90 text-black text-sm font-semibold rounded-lg hover:shadow-lg transition-all duration-300 hover:scale-105"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                <span>Get Started Free</span>
              </a>
            </div>
          </div>
        )} */}
      </div>

      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.7;
          }
        }
        
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </footer>
  );
};

export default Footer;